import { useMemo, useState } from 'react';

import { AsyncBoundary, Drawer, Icon, useToast } from '@kollez/design-system';
import { useHodDepartment } from '@/lib/queries';

/**
 * /hod/department (F86b) — direct port of kollez/pages/hod/department.html.
 *   Header · 4-band .ready stacked bar · "Students who need a push" segmented table
 *   Right column: skill-gap chart · mock-interview timeline
 *   Bulk-bar with mock/nudge actions
 */
export function HodDepartmentPage() {
  const { data, isLoading, error, refetch } = useHodDepartment();
  const toast = useToast();
  const [seg, setSeg] = useState<'almost' | 'none'>('almost');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mockOpen, setMockOpen] = useState(false);
  const [names, setNames] = useState<string[]>([]);

  const readiness = data?.readiness ?? { placed: 0, ready: 0, almost: 0, none: 0 };
  const total = readiness.placed + readiness.ready + readiness.almost + readiness.none;
  const atRisk = data?.atRisk ?? [];
  const gaps = data?.skillGaps ?? [];

  const shown = useMemo(() => {
    // Server returns at-risk together; group by "reason" length as a rough proxy for seg
    return atRisk.filter((r) => (seg === 'almost' ? !/(no application|hall ticket)/i.test(r.reason) : /(no application|hall ticket)/i.test(r.reason)));
  }, [atRisk, seg]);

  function toggleSel(id: string) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }

  const maxDrivesAsking = Math.max(1, ...gaps.map((g) => g.drivesAsking));

  return (
    <div className="stack" style={{ paddingBottom: 80 }}>
      <style>{`
        .ready { display: flex; height: 28px; border-radius: 6px; overflow: hidden; }
        .ready span { display: grid; place-items: center; font-size: .75rem; font-weight: 600; color: #fff; min-width: 28px; }
        .gap-row { display: grid; grid-template-columns: 110px minmax(0,1fr) 64px; gap: 10px; align-items: center; font-size: .875rem; }
        .gap-bars { display: grid; gap: 3px; }
        .gap-bars i { display: block; height: 6px; border-radius: 3px; }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">{data?.department?.name ?? 'Department'} · batch 2026 · {total} students</p>
          <h1 className="h-page">Getting {data?.department?.code ?? 'your dept'} ready</h1>
          <p className="subhead">
            {readiness.placed} placed ({data?.placementRate ?? 0}%, target 60%).
            {' '}{readiness.almost + readiness.none} students aren't ready to interview yet — most need a mock interview or a project.
          </p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-outline"><Icon name="clipboard-check" size={14} />Skills check</button>
          <button className="btn btn-primary" onClick={() => { setNames([]); setMockOpen(true); }}>
            <Icon name="calendar-plus" size={14} />Schedule mock interviews
          </button>
        </div>
      </div>

      <AsyncBoundary
        kind="detail"
        loading={isLoading}
        error={error}
        empty={!data}
        onRetry={() => void refetch()}
      >
        <section className="card card-pad grid gap-3">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="h-section">Readiness</h2>
            <span className="helper">Ready = verified profile, resume, a project and mock score 6+</span>
          </div>
          <div className="ready" role="img" aria-label={`${readiness.placed} placed, ${readiness.ready} ready, ${readiness.almost} almost, ${readiness.none} not started`}>
            {readiness.placed > 0 && <span style={{ flex: readiness.placed, background: 'rgb(var(--ink-success))' }}>{readiness.placed}</span>}
            {readiness.ready > 0 && <span style={{ flex: readiness.ready, background: 'rgb(var(--ink-accent))' }}>{readiness.ready}</span>}
            {readiness.almost > 0 && <span style={{ flex: readiness.almost, background: 'rgb(var(--ink-warning))' }}>{readiness.almost}</span>}
            {readiness.none > 0 && <span style={{ flex: readiness.none, background: 'rgb(var(--ink-danger))' }}>{readiness.none}</span>}
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-ink-secondary">
            <span><span className="dot dot-success" /> Placed</span>
            <span><span className="dot dot-accent" /> Ready</span>
            <span><span className="dot dot-warning" /> Almost ready</span>
            <span><span className="dot dot-danger" /> Not started</span>
          </div>
        </section>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px] items-start">
          <section className="card min-w-0">
            <div className="filterbar">
              <h2 className="h-section mr-2">Students who need a push</h2>
              <div className="segmented">
                <button aria-pressed={seg === 'almost'} onClick={() => setSeg('almost')}>Almost ready {readiness.almost}</button>
                <button aria-pressed={seg === 'none'} onClick={() => setSeg('none')}>Not started {readiness.none}</button>
              </div>
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th className="w-check"><span className="sr-only">Select</span></th>
                    <th>Student</th>
                    <th>Missing</th>
                    <th className="hide-mobile">Mentor</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.userId} aria-selected={selected.has(r.userId)}>
                      <td className="w-check">
                        <input type="checkbox" className="check" checked={selected.has(r.userId)} onChange={() => toggleSel(r.userId)} aria-label={`Select ${r.name}`} />
                      </td>
                      <td>
                        <div className="cell-title">{r.name}</div>
                        <div className="cell-sub">{r.roll ?? '—'}</div>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {r.reason.split(',').map((m, i) => (
                            <span key={i} className={`badge ${/score/i.test(m) ? 'badge-danger' : 'badge-warning'}`}>{m.trim()}</span>
                          ))}
                        </div>
                      </td>
                      <td className="hide-mobile">Not assigned</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {shown.length === 0 && (
                <div className="p-8 text-center text-ink-tertiary">Everyone in this bucket is on track.</div>
              )}
            </div>
          </section>

          <div className="grid gap-5 min-w-0">
            <section className="card card-pad grid gap-4">
              <div>
                <h2 className="h-section">Skills companies ask for</h2>
                <p className="helper mt-0.5">Open drives asking · students who have it</p>
              </div>
              <div className="grid gap-3">
                {gaps.map((g) => (
                  <div key={g.skill} className="gap-row">
                    <span>{g.skill}</span>
                    <div className="gap-bars">
                      <i style={{ width: `${(g.drivesAsking / maxDrivesAsking) * 100}%`, background: 'rgb(var(--ink-accent))' }} />
                      <i style={{ width: `${g.studentsHavingPct}%`, background: 'rgb(var(--ink-success))' }} />
                    </div>
                    <span className="helper text-right">{g.drivesAsking} · {g.studentsHavingPct}%</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-4 text-xs text-ink-tertiary">
                <span><span className="dot dot-accent" /> Drives asking</span>
                <span><span className="dot dot-success" /> Students with it</span>
              </div>
            </section>

            <section className="card">
              <div className="card-head"><h2 className="h-section">Mock interviews this week</h2></div>
              <div className="card-pad">
                <ol className="timeline">
                  <li data-state="done"><span className="t-dot" /><div className="t-title">Section A</div><div className="t-meta">Mon · avg score 6.8</div></li>
                  <li data-state="current"><span className="t-dot" /><div className="t-title">Section B</div><div className="t-meta">Today 4:30 pm · Placement cell</div></li>
                  <li><span className="t-dot" /><div className="t-title">Alumni panel</div><div className="t-meta">Sat 10 am · online</div></li>
                </ol>
              </div>
            </section>
          </div>
        </div>
      </AsyncBoundary>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div className="bulkbar" data-open="true">
          <span className="text-sm whitespace-nowrap"><b>{selected.size}</b> selected</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setNames(atRisk.filter((r) => selected.has(r.userId)).map((r) => r.name));
              setMockOpen(true);
            }}
          >
            <Icon name="calendar-plus" size={14} />Book mock interview
          </button>
          <button
            className="btn btn-ghost btn-sm"
            disabled
            title="Bulk nudge lands with /nexie/draft + /messages/send bulk path in a follow-up"
          >
            <Icon name="message-circle" size={14} />Nudge · soon
          </button>
        </div>
      )}

      <Drawer open={mockOpen} onClose={() => setMockOpen(false)} title="Mock interviews" size="lg">
        <p className="eyebrow">Mock interviews</p>
        <h2 className="h-section">{names.length ? `Book ${names.length} students` : 'Schedule a session'}</h2>
        {names.length > 0 && <p className="helper m-0 mt-2">{names.join(', ')}</p>}
        <label className="grid gap-1.5 mt-4">
          <span className="label">When</span>
          <input className="input" type="datetime-local" defaultValue="2026-09-19T15:00" />
        </label>
        <label className="grid gap-1.5 mt-4">
          <span className="label">Format</span>
          <select className="select">
            <option>Technical (company style)</option>
            <option>HR</option>
            <option>Group discussion</option>
          </select>
        </label>
        <div className="ai-suggest mt-4">
          <Icon name="sparkles" size={14} />
          <div>Nexie can prepare questions from the drives these students applied to. You review them first.</div>
        </div>
        <div className="drawer-foot">
          <button className="btn btn-outline" onClick={() => setMockOpen(false)}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled
            title="Bulk mock booking wires through /mock/schedule when it lands. Book individual mocks from the student's /mock page for now."
          >
            Book and notify · soon
          </button>
        </div>
      </Drawer>
    </div>
  );
}
